import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { X, Send, Sparkles } from 'lucide-react';
import { useEduBorgVisibility } from './useEduBorgVisibility';
import { useAuth } from '@/lib/auth-store';
import { cn } from '@/lib/utils';

type ChatTurn = { role: 'user' | 'assistant'; content: string; simulated?: boolean };

/**
 * Persistent EduBorg companion: small floating mascot in the bottom-right
 * corner. Click to expand a side panel for chat + suggestions + actions.
 *
 * Phase-1 scope:
 *   - Visibility-gated render via useEduBorgVisibility (anonymous users see
 *     nothing; later phases gate by org plan + role).
 *   - Idle animation + occasional "whisper" bubble for proactive suggestions.
 *   - Side panel UI shell — chat surface stubbed (no real backend yet).
 *
 * The whisper queue is local-only for now. Phase 5 wires it to the EduBorg
 * Agent rules engine running in Workers (server-pushed via SSE / WS).
 */
export function EduBorgCompanion() {
  const { visible, quietMode } = useEduBorgVisibility();
  const reduce = useReducedMotion();
  const [open, setOpen] = useState(false);
  const [whisper, setWhisper] = useState<string | null>(null);

  // Demo whispers — these are placeholders until the Agent rules engine lands.
  useEffect(() => {
    if (!visible || quietMode || open) {
      setWhisper(null);
      return;
    }
    const messages = [
      "I'm watching for student activity — I'll flag risks here.",
      'Tip: drag a lesson into the calendar to schedule it instantly.',
      'I can draft your weekly course pulse digest whenever you like.',
    ];
    let i = 0;
    const id = window.setInterval(() => {
      setWhisper(messages[i % messages.length] ?? null);
      i++;
      window.setTimeout(() => setWhisper(null), 6000);
    }, 14000);
    return () => window.clearInterval(id);
  }, [visible, quietMode, open]);

  if (!visible) return null;

  return (
    <>
      {/* Floating mascot trigger */}
      <div className="pointer-events-none fixed bottom-4 right-4 z-40 flex flex-col items-end gap-2 sm:bottom-6 sm:right-6">
        <AnimatePresence>
          {whisper && !open && (
            <motion.div
              key={whisper}
              role="status"
              aria-live="polite"
              initial={{ opacity: 0, y: 8, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 4, scale: 0.96 }}
              transition={{ duration: 0.25 }}
              className="pointer-events-auto max-w-xs rounded-2xl border border-border bg-card px-3.5 py-2 text-xs font-medium text-foreground shadow-lg"
            >
              <div className="flex items-start gap-2">
                <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent" aria-hidden />
                <span>{whisper}</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open EduBorg"
          aria-expanded={open}
          className="pointer-events-auto group relative flex h-14 w-14 items-center justify-center rounded-full bg-card shadow-lg ring-1 ring-border transition-shadow hover:shadow-xl"
          {...(reduce ? {} : { animate: { y: [0, -3, 0] } })}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
        >
          <span
            aria-hidden
            className="absolute inset-0 rounded-full bg-gradient-to-br from-accent/30 via-transparent to-transparent opacity-0 transition-opacity group-hover:opacity-100"
          />
          <img
            src="/brand/eduborg-mascot.png"
            alt=""
            aria-hidden
            width={48}
            height={48}
            className="relative z-10"
          />
          <span aria-hidden className="absolute -right-0.5 -top-0.5 flex h-3.5 w-3.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent/70 opacity-70" />
            <span className="relative inline-flex h-3.5 w-3.5 rounded-full bg-accent ring-2 ring-card" />
          </span>
        </motion.button>
      </div>

      {/* Side panel */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-40 bg-foreground/30 backdrop-blur-sm"
              onClick={() => setOpen(false)}
            />
            <motion.aside
              role="dialog"
              aria-label="EduBorg companion"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 320, damping: 32 }}
              className="fixed bottom-0 right-0 top-0 z-50 flex w-full max-w-md flex-col border-l border-border bg-background shadow-2xl"
            >
              <header className="flex items-center justify-between border-b border-border px-5 py-3.5">
                <div className="flex items-center gap-2.5">
                  <img
                    src="/brand/eduborg-mascot.png"
                    alt=""
                    aria-hidden
                    width={32}
                    height={32}
                    className="animate-mascot-idle"
                  />
                  <div>
                    <div className="font-display text-sm font-semibold">EduBorg</div>
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                      always on
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Close"
                  className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                >
                  <X className="h-4 w-4" aria-hidden />
                </button>
              </header>

              <ChatPanel />
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

/**
 * ChatPanel — wired to POST /ai/eduborg-respond. The Phase-5 cut.
 *
 * Keeps history in component state (resets on page reload). A persistent
 * conversation history that survives reloads will live in D1 and ride the
 * existing conversations table once the companion gets a "course-room"
 * conversation per user.
 */
function ChatPanel() {
  const { state } = useAuth();
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const token = state.status === 'authenticated' ? state.token : '';

  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight });
  }, [turns.length]);

  async function send() {
    const message = draft.trim();
    if (!message || sending || !token) return;
    setSending(true);
    setDraft('');
    // Capture history BEFORE we mutate state — we want what's on screen now.
    const history = turns.slice(-10).map((t) => ({ role: t.role, content: t.content }));
    setTurns((t) => [...t, { role: 'user', content: message }]);
    try {
      const apiBase = (import.meta.env.VITE_API_BASE_URL ?? '/api').replace(/\/$/, '');
      const res = await fetch(`${apiBase}/ai/eduborg-respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message, history }),
        credentials: 'include',
      });
      const json = (await res.json().catch(() => ({}))) as {
        reply?: string;
        simulated?: boolean;
        error?: string;
      };
      if (!res.ok) {
        const errMsg =
          json.error === 'ai_access_denied'
            ? "I'm tapped out for now — your AI credits are used up for this period."
            : `Something went wrong (${json.error ?? res.status}). Try again in a moment.`;
        setTurns((t) => [...t, { role: 'assistant', content: errMsg }]);
        return;
      }
      setTurns((t) => [
        ...t,
        {
          role: 'assistant',
          content: json.reply ?? '(no reply)',
          ...(json.simulated ? { simulated: true } : {}),
        },
      ]);
    } catch (err) {
      setTurns((t) => [
        ...t,
        { role: 'assistant', content: `Network error: ${String(err).slice(0, 100)}` },
      ]);
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <div ref={scrollerRef} className="flex-1 space-y-3 overflow-y-auto px-5 py-5">
        {turns.length === 0 ? (
          <div className="rounded-xl border border-border bg-card p-3.5 text-sm">
            <div className="font-semibold">Hi — I'm EduBorg.</div>
            <div className="mt-1 text-xs text-muted-foreground">
              Ask me about your courses, drafts, deadlines, or anything else. I'm proactive — I'll
              suggest things in the corner, too.
            </div>
          </div>
        ) : (
          turns.map((t, i) => (
            <div
              key={i}
              className={cn(
                'max-w-[85%] rounded-2xl px-3 py-2 text-sm',
                t.role === 'user'
                  ? 'ml-auto bg-primary text-primary-foreground'
                  : 'bg-secondary text-foreground',
              )}
            >
              <div className="whitespace-pre-wrap break-words">{t.content}</div>
              {t.simulated && (
                <div className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                  simulated · set AIML_API_KEY
                </div>
              )}
            </div>
          ))
        )}
        {sending && (
          <div className="text-xs italic text-muted-foreground">EduBorg is thinking…</div>
        )}
      </div>

      <form
        className="border-t border-border bg-card/50 p-3"
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        <label className="sr-only" htmlFor="eduborg-chat">
          Ask EduBorg
        </label>
        <div className="flex items-center gap-2 rounded-xl border border-input bg-background px-3 py-2 transition focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/30">
          <input
            id="eduborg-chat"
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Ask EduBorg…"
            disabled={sending}
            autoComplete="off"
            className="flex-1 bg-transparent text-sm outline-none disabled:cursor-not-allowed disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={!draft.trim() || sending}
            className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-primary text-primary-foreground disabled:cursor-not-allowed disabled:opacity-60"
            aria-label="Send"
          >
            <Send className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
      </form>
    </>
  );
}
